import { type ReactNode, useState } from 'react';
import { Col } from '../col';
import { Image } from '../image';
import { Row } from '../row';
import { Attachment, type AttachmentProperties, type AttachmentVariant } from './attachment';
import { useImageAttachments } from './hooks';

type P = {
  attachments: Array<{ id: string } & Omit<AttachmentProperties, 'variant'>>;
  columns?: 1 | 2;
  extra?: ReactNode;
  title?: ReactNode;
  variant?: AttachmentVariant;
};

const Attachments = ({ attachments, columns = 2, extra, title, variant }: P) => {
  const columnSpan = columns === 1 ? 24 : 12;
  const [previewId, setPreviewId] = useState<null | string>(null);
  const { imageAttachments, isImageAttachment } = useImageAttachments({ attachments });
  const currentIndex = previewId ? imageAttachments.findIndex((attachment) => attachment.id === previewId) : 0;

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Image.PreviewGroup
      preview={{
        current: currentIndex,
        onChange(current) {
          const attachment = imageAttachments[current];
          if (attachment) {
            setPreviewId(attachment.id);
          }
        },
        onOpenChange(opened) {
          if (!opened) {
            setPreviewId(null);
          }
        },
        open: previewId !== null,
      }}
    >
      <Row gutter={[0, 'xs']}>
        {Boolean(title) || Boolean(extra) ? (
          <Col span={24}>
            <Row justify="space-between">
              {title ? <Col>{title}</Col> : null}
              {extra ? <Col>{extra}</Col> : null}
            </Row>
          </Col>
        ) : null}

        <Col span={24}>
          <Row gutter={['xs', 'xs']}>
            {attachments.map((attachment) => (
              <Col key={attachment.id} span={columnSpan}>
                <Attachment
                  {...attachment}
                  onClick={(event) => {
                    if (isImageAttachment({ attachmentId: attachment.id })) {
                      event.preventDefault();
                      setPreviewId(attachment.id);
                    }
                  }}
                  variant={variant}
                />
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </Image.PreviewGroup>
  );
};

export default Attachments;
